import { useEffect, useState } from "react";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileSpreadsheet, FileText, Scroll, Download } from "lucide-react";
import { toast } from "sonner";

export default function ReportsPage() {
  const [dusuns, setDusuns] = useState([]);
  const [rwOpts, setRwOpts] = useState([]);
  const [rtOpts, setRtOpts] = useState([]);
  const [tpsList, setTpsList] = useState([]);
  const [scope, setScope] = useState({ dusun: "all", rw: "all", rt: "all", tps: "all", doc_type: "DPT" });
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    api.get("/dusuns").then(({ data }) => setDusuns(data || []));
    api.get("/tps").then(({ data }) => setTpsList(data || []));
  }, []);

  useEffect(() => {
    if (scope.dusun === "all") { setRwOpts([]); return; }
    api.get("/wilayah/options", { params: { dusun: scope.dusun } })
      .then(({ data }) => setRwOpts(data.rws || []));
  }, [scope.dusun]);

  useEffect(() => {
    if (scope.dusun === "all" || scope.rw === "all") { setRtOpts([]); return; }
    api.get("/wilayah/options", { params: { dusun: scope.dusun, rw: scope.rw } })
      .then(({ data }) => setRtOpts(data.rts || []));
  }, [scope.dusun, scope.rw]);

  const params = () => {
    const p = { doc_type: scope.doc_type };
    if (scope.dusun !== "all") p.dusun = scope.dusun;
    if (scope.rw !== "all") p.rw = scope.rw;
    if (scope.rt !== "all") p.rt = scope.rt;
    if (scope.tps !== "all") p.tps = scope.tps;
    return p;
  };

  const download = async (kind) => {
    setDownloading(kind);
    try {
      const url = kind === "ba" ? "/reports/berita-acara" : "/exports/voters";
      const p = params();
      if (kind === "xlsx") p.fmt = "xlsx";
      if (kind === "pdf") p.fmt = "pdf";
      const res = await api.get(url, { params: p, responseType: "blob" });
      const blob = new Blob([res.data], { type: res.headers["content-type"] });
      const cd = res.headers["content-disposition"] || "";
      const m = /filename="?([^"]+)"?/.exec(cd);
      const filename = m ? m[1] : `${scope.doc_type}.${kind === "xlsx" ? "xlsx" : "pdf"}`;
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(link.href), 1000);
      toast.success("File diunduh");
    } catch (e) {
      // Axios responseType="blob" → error body is a Blob; parse it to JSON for friendly message
      let msg = formatApiError(e);
      try {
        const data = e?.response?.data;
        if (data && typeof data.text === "function") {
          const txt = await data.text();
          try {
            const parsed = JSON.parse(txt);
            if (parsed?.detail) msg = typeof parsed.detail === "string" ? parsed.detail : JSON.stringify(parsed.detail);
          } catch { /* not JSON */ }
        }
      } catch { /* noop */ }
      toast.error(msg);
    }
    finally { setDownloading(null); }
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="reports-page">
      <header>
        <div className="text-xs uppercase tracking-[0.25em] text-slate-500 font-semibold">Laporan &amp; Cetak</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1">Export &amp; Berita Acara</h1>
        <p className="text-slate-600 text-sm mt-2 max-w-2xl">
          Cetak DPS/DPT dalam format Excel atau PDF, serta hasilkan Berita Acara penetapan secara otomatis.
          Filter cakupan area sesuai kebutuhan.
        </p>
      </header>

      <section className="rounded-md border border-slate-200 bg-white p-6 space-y-4">
        <div className="text-xs uppercase tracking-[0.25em] text-slate-500 font-semibold">Cakupan</div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <Field label="Jenis Dokumen">
            <Select value={scope.doc_type} onValueChange={(v) => setScope({ ...scope, doc_type: v })}>
              <SelectTrigger className="h-11" data-testid="report-doctype"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="DPS">DPS (Sementara)</SelectItem>
                <SelectItem value="DPSHP">DPSHP (Hasil Perbaikan)</SelectItem>
                <SelectItem value="DPT">DPT (Tetap)</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Dusun">
            <Select value={scope.dusun} onValueChange={(v) => setScope({ ...scope, dusun: v, rw: "all", rt: "all" })}>
              <SelectTrigger className="h-11" data-testid="report-dusun"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Seluruh Desa</SelectItem>
                {dusuns.map((d) => <SelectItem key={d.id} value={d.nama}>{d.nama}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="RW">
            <Select value={scope.rw} onValueChange={(v) => setScope({ ...scope, rw: v, rt: "all" })} disabled={scope.dusun === "all"}>
              <SelectTrigger className="h-11" data-testid="report-rw"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua RW</SelectItem>
                {rwOpts.map((rw) => <SelectItem key={rw} value={rw}>RW {rw}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="RT">
            <Select value={scope.rt} onValueChange={(v) => setScope({ ...scope, rt: v })} disabled={scope.rw === "all"}>
              <SelectTrigger className="h-11" data-testid="report-rt"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua RT</SelectItem>
                {rtOpts.map((rt) => <SelectItem key={rt} value={rt}>RT {rt}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="TPS">
            <Select value={scope.tps} onValueChange={(v) => setScope({ ...scope, tps: v })}>
              <SelectTrigger className="h-11" data-testid="report-tps"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua TPS</SelectItem>
                {tpsList.map((t) => <SelectItem key={t.id} value={t.kode}>{t.kode}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
        </div>
      </section>

      <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <ReportCard
          icon={FileSpreadsheet}
          tone="emerald"
          title="Daftar Pemilih (Excel)"
          desc="Format .xlsx siap diolah lebih lanjut atau dicetak."
          onClick={() => download("xlsx")}
          loading={downloading === "xlsx"}
          testid="download-xlsx"
        />
        <ReportCard
          icon={FileText}
          tone="red"
          title="Daftar Pemilih (PDF)"
          desc="Tabel rapi landscape A4, siap ditandatangani."
          onClick={() => download("pdf")}
          loading={downloading === "pdf"}
          testid="download-pdf"
        />
        <ReportCard
          icon={Scroll}
          tone="slate"
          title="Berita Acara (PDF)"
          desc="Berita acara penetapan dengan ringkasan jumlah pemilih dan kolom tanda tangan."
          onClick={() => download("ba")}
          loading={downloading === "ba"}
          testid="download-ba"
        />
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold">{label}</Label>
      {children}
    </div>
  );
}

function ReportCard({ icon: Icon, title, desc, onClick, loading, tone = "slate", testid }) {
  const tones = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    red: "bg-red-50 border-red-200 text-red-900",
    slate: "bg-slate-900 text-white border-slate-900",
  };
  const isDark = tone === "slate";
  return (
    <article className={`rounded-md border p-5 flex flex-col ${tones[tone]}`}>
      <Icon className={`h-7 w-7 ${isDark ? "text-white" : ""}`} />
      <h3 className="font-display font-bold text-lg mt-3">{title}</h3>
      <p className={`text-sm mt-1 ${isDark ? "text-slate-300" : "opacity-80"}`}>{desc}</p>
      <Button
        onClick={onClick}
        disabled={loading}
        className={`mt-4 font-semibold ${isDark ? "bg-white text-slate-900 hover:bg-slate-100" : "bg-slate-900 hover:bg-slate-800 text-white"}`}
        data-testid={testid}
      >
        <Download className="h-4 w-4 mr-2" /> {loading ? "Mengunduh..." : "Unduh"}
      </Button>
    </article>
  );
}
